/**
 * Plans what a new teeth selection implies: which products to keep, move,
 * create and delete, for a group of teeth groups configured together.
 *
 * The group's leader is the product the dentist is on, the one in the URL path;
 * the others inherit its configuration.
 *
 * planTeethSelection takes every decision, the rest reads its result.
 */

export type BulkTeethProduct = {
  id: string;
  teeth: ReadonlyArray<number>;
};

export type BulkTeethAssignment<TProduct> = {
  teeth: number[];
  product: TProduct | null;
};

/**
 * One assignment per selected teeth group, ascending, plus the leftover products.
 * An assignment with no product is a teeth group to create one for.
 */
export type BulkTeethPlan<TProduct> = {
  assignments: BulkTeethAssignment<TProduct>[];
  productsToDelete: TProduct[];
};

export type BulkTeethReassignment<TProduct> = {
  product: TProduct;
  teeth: number[];
};

/** A group split the way the URL carries it: leader in the path, followers in `?bulk=`. */
export type BulkGroup = {
  leaderProductId: string;
  bulkProductFollowerIds: string[];
};

const byAscending = (first: number, second: number) => first - second;

const toothOf = (product: BulkTeethProduct) => product.teeth[0] ?? 0;

const byTooth = (first: BulkTeethProduct, second: BulkTeethProduct) =>
  toothOf(first) - toothOf(second);

/**
 * Puts the products of the group on a new teeth selection, taken raw:
 * duplicates and any order are fine.
 *
 * A product whose teeth group stays selected never moves; the others are moved onto
 * the teeth groups left uncovered, in ascending order, rather than deleted and
 * recreated, so the configuration already entered on them survives. Only the
 * surplus is left to delete.
 */
export function planTeethSelection<TProduct extends BulkTeethProduct>({
  selectedTeethGroups,
  products,
}: {
  selectedTeethGroups: ReadonlyArray<ReadonlyArray<number>>;
  products: ReadonlyArray<TProduct>;
}): BulkTeethPlan<TProduct> {
  const sortedSelectedTeethGroups = [
    ...new Set(
      selectedTeethGroups.map(group => [...group].sort(byAscending).join(","))
    ),
  ].map(group => group.split(",").map(Number).sort(byAscending));

  const assignments: BulkTeethAssignment<TProduct>[] = [];
  const unassignedProducts: TProduct[] = [];

  for (const product of [...products].sort(byTooth)) {
    const matchingSelectedGroupIndex = sortedSelectedTeethGroups.findIndex(
      group =>
        group.length === product.teeth.length &&
        group.every(tooth => product.teeth.includes(tooth))
    );

    const matchingSelectedGroup =
      matchingSelectedGroupIndex >= 0
        ? sortedSelectedTeethGroups[matchingSelectedGroupIndex]
        : undefined;

    if (matchingSelectedGroupIndex >= 0) {
      sortedSelectedTeethGroups.splice(matchingSelectedGroupIndex, 1);
    }

    if (matchingSelectedGroup) {
      assignments.push({
        teeth: matchingSelectedGroup,
        product,
      });
    } else {
      unassignedProducts.push(product);
    }
  }

  const teethGroupsWithoutAssignment = sortedSelectedTeethGroups.filter(
    group =>
      !assignments.some(
        assignment =>
          assignment.teeth.length === group.length &&
          assignment.teeth.every(tooth => group.includes(tooth))
      )
  );

  const reuseCount = Math.min(
    teethGroupsWithoutAssignment.length,
    unassignedProducts.length
  );

  teethGroupsWithoutAssignment.forEach((group, index) => {
    assignments.push({
      teeth: group,
      product: reuseCount > index ? unassignedProducts[index] : null,
    });
  });

  return {
    assignments: assignments.sort((first, second) =>
      byAscending(first.teeth[0], second.teeth[0])
    ),
    productsToDelete: unassignedProducts.slice(reuseCount),
  };
}

/** Teeth groups no product could be reused for. */
export const getTeethGroupsToCreate = <TProduct extends BulkTeethProduct>(
  plan: BulkTeethPlan<TProduct>
): number[][] =>
  plan.assignments
    .filter(({ product }) => !product)
    .map(({ teeth }) => teeth.sort(byAscending))
    .sort((first, second) => byAscending(first[0], second[0]));

/** Products to move onto another teeth group, paired with their new teeth group. */
export const getReassignments = <TProduct extends BulkTeethProduct>(
  plan: BulkTeethPlan<TProduct>
): BulkTeethReassignment<TProduct>[] =>
  plan.assignments.filter(
    (assignment): assignment is BulkTeethReassignment<TProduct> =>
      !!assignment.product &&
      (assignment.product.teeth.length !== assignment.teeth.length ||
        assignment.product.teeth.some(
          tooth => !assignment.teeth.includes(tooth)
        ))
  );

/** Teeth the group covered but no longer covers, to confirm before deleting. */
export const getRemovedTeeth = <TProduct extends BulkTeethProduct>({
  plan,
  products,
}: {
  plan: BulkTeethPlan<TProduct>;
  products: ReadonlyArray<TProduct>;
}): number[] => {
  const remainingTeeth = new Set(
    plan.assignments.map(({ teeth }) => teeth).flat()
  );

  return products
    .flatMap(({ teeth }) => [...teeth])
    .filter(tooth => !remainingTeeth.has(tooth))
    .sort(byAscending);
};

/**
 * Rebuilds the group to navigate to, from the surviving products and the ones
 * just created. The leader keeps the lead if it survived, even moved to another
 * teeth group, so the dentist stays on the product they were configuring; otherwise
 * the product on the lowest teeth group takes over. Null when nothing survived.
 */
export function resolveBulkGroup<TProduct extends BulkTeethProduct>({
  plan,
  leaderProductId,
  createdProducts,
}: {
  plan: BulkTeethPlan<TProduct>;
  leaderProductId: string;
  createdProducts: ReadonlyArray<BulkTeethProduct>;
}): BulkGroup | null {
  const createdProductIdsByTooth = new Map(
    createdProducts.map(product => [toothOf(product), product.id])
  );

  const survivingProductIds = plan.assignments.flatMap(({ teeth, product }) => {
    const productId = product?.id ?? createdProductIdsByTooth.get(teeth[0]);

    return productId ? [productId] : [];
  });

  const [productIdOnLowestTooth] = survivingProductIds;

  if (!productIdOnLowestTooth) {
    return null;
  }

  const survivingLeaderId = survivingProductIds.includes(leaderProductId)
    ? leaderProductId
    : productIdOnLowestTooth;

  return {
    leaderProductId: survivingLeaderId,
    bulkProductFollowerIds: survivingProductIds.filter(
      productId => productId !== survivingLeaderId
    ),
  };
}
